import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import logo from './../assets/logo_2-removebg-preview.png';
import { Menu, X, Sparkles, ChevronDown } from 'lucide-react';
import {
  IconHome,
  IconBuilding,
  IconDeviceDesktopStar,
  IconDeviceLaptop,
  IconBriefcase,
  IconNews,
  IconPhone,
  IconPackage,
  IconBrush,
  IconCode,
  IconBrandShopee,
  IconTrendingUp,
  IconServer,
  IconDeviceMobile,
  IconDatabase,
  IconBrandWordpress,
  IconSeo,
  IconShare,
  IconSearch,
  IconBuildingStore,
  IconShoppingCart,
  IconLayout,
  IconPalette,
  IconVectorTriangle,
  IconNetwork,
  IconCloud,
} from "@tabler/icons-react";
import { productsData } from "../data/productsData";

// ─── Services mega-menu data ──────────────────────────────────────────────────
const servicesData = [
  {
    category: 'Design & Branding',
    items: [
      { label: 'Logo Design',        icon: IconPalette,        to: '/contact',   color: '#f59e0b' },
      { label: 'Graphic Design',     icon: IconVectorTriangle, to: '/contact',   color: '#ec4899' },
      { label: 'Web Design',         icon: IconLayout,         to: '/contact',   color: '#8b5cf6' },
      { label: 'UI/UX Services',     icon: IconDeviceDesktopStar, to: '/contact', color: '#6366f1' },
    ],
  },
  {
    category: 'Development',
    items: [
      { label: 'Web Development',    icon: IconCode,           to: '/contact',   popular: true,  color: '#3b82f6' },
      { label: 'Mobile App Dev',     icon: IconDeviceMobile,   to: '/contact',                   color: '#06b6d4' },
      { label: 'Custom ERP, CRM & Solutions', icon: IconDatabase, to: '/contact',               color: '#14b8a6' },
    ],
  },
  {
    category: 'Marketing & Growth',
    items: [
      { label: 'SEO Services',       icon: IconSeo,            to: '/contact',   popular: true,  color: '#a855f7' },
      { label: 'Social Media',       icon: IconShare,          to: '/contact',                   color: '#e11d48' },
      { label: 'Search Engine Marketing', icon: IconSearch,    to: '/contact',                   color: '#0ea5e9' },
    ],
  },
  {
    category: 'Infrastructure',
    items: [
      { label: 'Domain & DNS Management', icon: IconNetwork,   to: '/contact',                   color: '#2563eb' },
      { label: 'Cloud Hosting',      icon: IconCloud,          to: '/contact',   popular: true,  color: '#7c3aed' },
    ],
  },
];

const navLinks = [
  { label: 'Home',         to: '/',            icon: IconHome },
  { label: 'Services',     to: '/service',     icon: IconDeviceDesktopStar },
  { label: 'Industries',   to: '/industries',  icon: IconDeviceLaptop },
  // { label: 'Services',     to: '/service',     icon: IconDeviceDesktopStar },
  // { label: 'Case Studies', to: '/case-studies',icon: IconBriefcase },
  { label: 'Products',     to: '/products',    icon: IconPackage },
  { label: 'About Us',     to: '/about',       icon: IconBuilding },
  { label: 'Blogs',        to: '/blogs',       icon: IconNews },
  { label: 'Contact Us',   to: '/contact',     icon: IconPhone },
];

// ─── Services mega-menu dropdown (desktop) ───────────────────────────────────
const ServicesDropdown = () => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const location = useLocation();
  const isActive =
    location.pathname === '/service' ||
    location.pathname.startsWith('/service/');

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const panelW = 920;
    const left = Math.min(
      Math.max(r.left + r.width / 2 - panelW / 2, 8),
      window.innerWidth - panelW - 8
    );
    setPanelPos({ top: r.bottom + 12, left });
  }, []);

  // Close on outside click (button OR panel)
  useEffect(() => {
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleToggle = () => {
    updatePos();
    setOpen((v) => !v);
  };

  // columns: col0 → Design & Infra | col1 → Development | col2 → Marketing
  const col0 = servicesData.filter((_, i) => i === 0 || i === 3);
  const col1 = servicesData.filter((_, i) => i === 1);
  const col2 = servicesData.filter((_, i) => i === 2);

  const panel = open ? (
    <div
      ref={panelRef}
      className="bg-white rounded-2xl shadow-2xl border border-gray-100"
      style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, width: 920, zIndex: 2147483647 }}
    >
      {/* Header */}
      <div className="px-6 pt-5 pb-2">
        <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase">All Services</p>
      </div>

      {/* 3-column grid */}
      <div className="px-6 pb-4 grid grid-cols-3 gap-x-6">
        {[col0, col1, col2].map((col, ci) => (
          <div key={ci} className="flex flex-col gap-4">
            {col.map((group) => (
              <div key={group.category}>
                <p className="text-[11px] font-bold tracking-wider text-[#363090] uppercase mb-2">
                  {group.category}
                </p>
                <div className="flex flex-col gap-0.5">
                  {group.items.map(({ label, icon: Icon, to, popular, color }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm text-gray-700 hover:bg-[#363090]/5 hover:text-[#363090] transition-colors duration-150"
                    >
                      <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: color }}>
                        <Icon size={14} className="text-white" />
                      </span>
                      <span className="flex-1 truncate font-medium">{label}</span>
                      {popular && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#363090]/10 text-[#363090] leading-none">
                          Popular
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-6 py-3 flex justify-between items-center">
        <Link
          to="/service"
          onClick={() => setOpen(false)}
          className="text-sm font-semibold text-[#363090] hover:text-[#f04c27] transition-colors"
        >
          See all services →
        </Link>
        <Link
          to="/contact"
          onClick={() => setOpen(false)}
          className="text-sm text-gray-400 hover:text-[#363090] transition-colors"
        >
          Talk to our team →
        </Link>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className={`flex items-center gap-1 text-[13px] font-semibold whitespace-nowrap transition-colors duration-200 ${
          isActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <IconDeviceDesktopStar size={16} />
        Services
        <ChevronDown
          size={13}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {createPortal(panel, document.body)}
    </>
  );
};

// ─── Products dropdown (desktop) ─────────────────────────────────────────────
// Per-product meta for the mega-menu
const productsMeta = [
  {
    slug: 'distributor-crm-hotelpos-style',
    label: 'Distribution Management System',
    desc: 'Sales, procurement, inventory, finance & field ops in one platform.',
    tags: ['React', 'Node.js', 'MongoDB'],
    color: '#f97316',        // orange
    icon: IconDatabase,
    image: '/Distribuion Crm.jpg',
  },
  {
    slug: 'manufacturing-erp',
    label: 'Manufacturing Firm ERP',
    desc: 'Procurement, inventory, warehouse & Tally integration connected.',
    tags: ['React', 'Node.js', 'MySQL'],
    color: '#3b82f6',        // blue
    icon: IconBriefcase,
    image: '/product-images/manufacturing-erp/image2.png',
  },
  {
    slug: 'sfa-crm-app',
    label: 'SFA CRM App',
    desc: 'Mobile app for field teams — visits, orders, stock & payments.',
    tags: ['React Native', 'Firebase'],
    color: '#10b981',        // emerald
    icon: IconDeviceMobile,
    image: '/product-images/sfa-crm-app/image-8-1.png',
  },
  {
    slug: 'sfa-crm',
    label: 'SFA CRM',
    desc: 'Admin panel for field sales, dealer network & e-commerce, synced with Tally.',
    tags: ['React', 'Express', 'MongoDB'],
    color: '#8b5cf6',        // violet
    icon: IconPackage,
    image: '/Sfa.jpg',
  },
  {
    slug: 'hotel-pos',
    label: 'Hotel POS',
    desc: 'Room management, billing, restaurant orders & housekeeping in one platform.',
    tags: ['React', 'Node.js', 'MySQL'],
    color: '#0ea5e9',        // sky blue
    icon: IconBuildingStore,
    image: '/Pos.png',
  },
];

const ProductsDropdown = () => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const location = useLocation();
  const isActive =
    location.pathname === '/products' ||
    location.pathname.startsWith('/products/');

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const panelW = 1100;
    const left = Math.min(
      Math.max(r.left + r.width / 2 - panelW / 2, 8),
      window.innerWidth - panelW - 8
    );
    setPanelPos({ top: r.bottom + 12, left });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleToggle = () => { updatePos(); setOpen((v) => !v); };

  const panel = open ? (
    <div
      ref={panelRef}
      className="bg-white rounded-2xl shadow-2xl border border-gray-100"
      style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, width: 1100, zIndex: 2147483647 }}
    >
      {/* Header row */}
      <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-gray-100">
        <p className="text-[11px] font-bold tracking-widest text-gray-400 uppercase">Our Products</p>
        <Link
          to="/products"
          onClick={() => setOpen(false)}
          className="text-xs font-semibold text-[#363090] hover:text-[#f04c27] transition-colors"
        >
          View all →
        </Link>
      </div>

      {/* Product cards grid */}
      <div className="px-6 py-5 grid grid-cols-5 gap-5">
        {productsMeta.map(({ slug, label, desc, tags, color, icon: Icon, image }) => (
          <Link
            key={slug}
            to={`/products/${slug}`}
            onClick={() => setOpen(false)}
            className="group flex flex-col rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
          >
            {/* Image area */}
            <div className="relative h-40 overflow-hidden" style={{ background: `${color}15` }}>
              <img
                src={image}
                alt={label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              {/* Icon badge */}
              <span
                className="absolute top-2 left-2 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
                style={{ background: color }}
              >
                <Icon size={15} className="text-white" />
              </span>
            </div>

            {/* Content */}
            <div className="p-4 flex flex-col gap-2 flex-1">
              <p className="text-[13px] font-bold text-[#363090] group-hover:text-[#f04c27] transition-colors leading-tight">
                {label}
              </p>
              <p className="text-[11px] text-gray-500 leading-snug line-clamp-2">{desc}</p>
              {/* Tags */}
              <div className="flex flex-wrap gap-1 mt-auto pt-1">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                    style={{ background: `${color}18`, color }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-6 py-3 flex justify-between items-center">
        <Link
          to="/products"
          onClick={() => setOpen(false)}
          className="text-sm font-semibold text-[#363090] hover:text-[#f04c27] transition-colors"
        >
          See all products →
        </Link>
        <Link
          to="/contact"
          onClick={() => setOpen(false)}
          className="text-sm text-gray-400 hover:text-[#363090] transition-colors"
        >
          Request a demo →
        </Link>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className={`flex items-center gap-1 text-[13px] font-semibold whitespace-nowrap transition-colors duration-200 ${
          isActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <IconPackage size={16} />
        Products
        <ChevronDown
          size={13}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {createPortal(panel, document.body)}
    </>
  );
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const linkClass = ({ isActive }) =>
    `flex items-center gap-1 text-[13px] font-semibold whitespace-nowrap transition-colors duration-200 ${
      isActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
    }`;

  // Close mobile menu on navigation
  useEffect(() => {
    setMenuOpen(false);
    setProductsOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isProductsActive =
    location.pathname === '/products' ||
    location.pathname.startsWith('/products/');

  const isServicesActive =
    location.pathname === '/service' ||
    location.pathname.startsWith('/service/');

  return (
    <header className="fixed top-0 left-1/2 -translate-x-1/2 z-[2147483647] w-full max-w-6xl bg-white/10 backdrop-blur-lg border border-white/30 lg:rounded-full shadow-lg lg:top-4 lg:w-[90%]">
      <div className="relative flex items-center justify-between px-4 lg:px-8 py-3">
        {/* Logo */}
        <Link to="/" className="z-10">
          <img src={logo} alt="Parnets Logo" className="h-10 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-4 absolute left-1/2 -translate-x-1/2 flex-nowrap z-10">
          {navLinks.map(({ label, to, icon: Icon }) => {
            if (label === 'Services') {
              return <ServicesDropdown key={to} />;
            }
            if (label === 'Products') {
              return <ProductsDropdown key={to} />;
            }
            return (
              <NavLink key={to} to={to} end={to === '/'} className={linkClass}>
                <Icon size={16} />
                {label}
              </NavLink>
            );
          })}

          {/* Ask AI button — desktop */}
          <button
            onClick={() => navigate('/ask-ai')}
            className="relative flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-bold whitespace-nowrap text-white shadow-lg overflow-hidden group transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
            style={{ background: 'linear-gradient(135deg, #363090 0%, #f04c27 100%)' }}
          >
            <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" />
            <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" style={{ background: 'linear-gradient(135deg, #363090, #f04c27)' }} />
            <span className="relative flex items-center gap-2">
              <Sparkles size={15} className="animate-pulse" />
              Ask AI
            </span>
          </button>
        </nav>

        {/* Mobile: Ask AI + hamburger */}
        <div className="lg:hidden flex items-center gap-3">
          {/* Mobile Ask AI */}
          <button
            onClick={() => navigate('/ask-ai')}
            className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg overflow-hidden group transition-all duration-300 hover:scale-105 active:scale-95 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #363090 0%, #f04c27 100%)' }}
          >
            <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" />
            <span className="relative flex items-center gap-1.5">
              <Sparkles size={13} className="animate-pulse" />
              Ask AI
            </span>
          </button>

          <button
            className="text-[#363090]"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white/20 backdrop-blur-lg border-t border-white/30 rounded-b-xl px-[5vw] pb-5 pt-3 flex flex-col gap-4">
          {navLinks.map(({ label, to, icon: Icon }) => {
            if (label === 'Services') {
              return (
                <div key={to}>
                  <NavLink
                    to="/contact"
                    className={`flex items-center gap-2 text-base font-semibold transition-colors duration-200 ${
                      isServicesActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
                    }`}
                  >
                    <IconDeviceDesktopStar size={16} />
                    Services
                  </NavLink>
                </div>
              );
            }
            if (label === 'Products') {
              return (
                <div key={to}>
                  {/* Products row — link + chevron toggle */}
                  <div className="flex items-center justify-between">
                    <NavLink
                      to="/products"
                      className={`flex items-center gap-2 text-base font-semibold transition-colors duration-200 ${
                        isProductsActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
                      }`}
                    >
                      <IconPackage size={16} />
                      Products
                    </NavLink>
                    <button
                      onClick={() => setProductsOpen((v) => !v)}
                      className="text-[#363090] hover:text-[#f04c27] transition-colors p-1"
                      aria-label="Toggle products submenu"
                    >
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>

                  {/* Expandable sub-list */}
                  {productsOpen && (
                    <div className="mt-2 ml-6 flex flex-col gap-2">
                      {productsData.map((p) => (
                        <Link
                          key={p.slug}
                          to={`/products/${p.slug}`}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#f04c27] transition-colors duration-150 py-0.5"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ background: '#f04c27' }}
                          />
                          {p.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-2 text-base font-semibold transition-colors duration-200 ${
                    isActive ? 'text-[#f04c27]' : 'text-[#363090] hover:text-[#f04c27]'
                  }`
                }
              >
                <Icon size={16} />
                {label}
              </NavLink>
            );
          })}
        </div>
      )}
    </header>
  );
};

export default Header;
